import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Eye, MailPlus, ShieldCheck, TimerReset, Trash2 } from "lucide-react";
import { api, type Alert, type AlertDetail, type AlertRecipient, type Hotel, type Location } from "../api/client";

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [selectedAlertId, setSelectedAlertId] = useState("");
  const alerts = useQuery({ queryKey: ["alerts"], queryFn: () => api<Alert[]>("/api/admin/alerts") });
  const alertDetail = useQuery({
    queryKey: ["alert-detail", selectedAlertId],
    queryFn: () => api<AlertDetail>(`/api/admin/alerts/${selectedAlertId}`),
    enabled: Boolean(selectedAlertId)
  });
  const recipients = useQuery({ queryKey: ["alert-recipients"], queryFn: () => api<AlertRecipient[]>("/api/admin/alert-recipients") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => api<Location[]>("/api/admin/locations") });

  const scopedLocations = useMemo(() => (
    (locations.data ?? []).filter((location) => !selectedHotelId || location.hotelId === selectedHotelId)
  ), [locations.data, selectedHotelId]);

  const createRecipient = useMutation({
    mutationFn: (body: unknown) => api<AlertRecipient>("/api/admin/alert-recipients", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alert-recipients"] })
  });

  const updateRecipient = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api<AlertRecipient>(`/api/admin/alert-recipients/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ active })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alert-recipients"] })
  });

  const deleteRecipient = useMutation({
    mutationFn: (id: string) => api(`/api/admin/alert-recipients/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alert-recipients"] })
  });

  const updateAlert = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "acknowledge" | "resolve" }) => api<Alert>(`/api/admin/alerts/${id}/${action}`, {
      method: "POST",
      body: "{}"
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alerts"] })
  });

  function submitRecipient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createRecipient.mutate({
      hotelId: String(form.get("hotelId")),
      locationId: String(form.get("locationId") || "") || undefined,
      roleLabel: String(form.get("roleLabel")),
      email: String(form.get("email")),
      active: true,
      schedule: {}
    });
    event.currentTarget.reset();
    setSelectedHotelId("");
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card alerts-hero">
        <div>
          <span className="panel-eyebrow left">Red Flag Alerts</span>
          <h2>Kriticne ocjene idu dezurnom timu kroz SMTP.</h2>
          <p>Primaoci se scope-uju po objektu i lokaciji; worker ima fallback na role ako nema posebnog recipienta.</p>
        </div>
        <div className="object-brand-card">
          <BellRing size={34} />
          <strong>{alerts.data?.filter((alert) => ["new", "sent", "escalated"].includes(alert.status)).length ?? 0} otvoreno</strong>
          <span>Email SLA target: 15s</span>
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Alerti</h2>
              <p>Acknowledge i resolve akcije ostaju u audit logu.</p>
            </div>
          </div>
          <div className="rule-list">
            {(alerts.data ?? []).map((alert) => (
              <article className="rule-card" key={alert.id}>
                <div>
                  <strong>{alert.hotel.name} - {alert.location.name}</strong>
                  <span>{alert.reason}</span>
                </div>
                <div className="rule-meta">
                  <span>{alert.severity}</span>
                  <span>{alert.status}</span>
                  <span>{new Date(alert.createdAt).toLocaleString()}</span>
                  <span>{alert.sentAt ? "sent" : "not sent"}</span>
                </div>
                <div className="row-actions">
                  <button onClick={() => setSelectedAlertId(alert.id)}><Eye size={16} /> Detalji</button>
                  <button onClick={() => updateAlert.mutate({ id: alert.id, action: "acknowledge" })}><TimerReset size={16} /> Ack</button>
                  <button onClick={() => updateAlert.mutate({ id: alert.id, action: "resolve" })}><ShieldCheck size={16} /> Resolve</button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Primaoci</h2>
              <p>Lokacijski recipient ima prednost; hotelski recipient je fallback za taj objekat.</p>
            </div>
          </div>
          <form className="compact-form" onSubmit={submitRecipient}>
            <h3><MailPlus size={16} /> Novi recipient</h3>
            <select name="hotelId" value={selectedHotelId} onChange={(event) => setSelectedHotelId(event.target.value)} required>
              <option value="">Izaberi objekat</option>
              {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
            </select>
            <select name="locationId">
              <option value="">Cijeli objekat</option>
              {scopedLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
            <input name="roleLabel" placeholder="Npr. Dezurni menadzer" required />
            <input name="email" type="email" placeholder="manager@hotel.test" required />
            <button type="submit"><MailPlus size={16} /> Dodaj primaoca</button>
          </form>
          <div className="recipient-list">
            {(recipients.data ?? []).map((recipient) => (
              <article className="recipient-card" key={recipient.id}>
                <div>
                  <strong>{recipient.roleLabel}</strong>
                  <span>{recipient.email}</span>
                  <small>{recipient.hotel.name} - {recipient.location?.name ?? "cijeli objekat"}</small>
                </div>
                <div className="row-actions">
                  <button onClick={() => updateRecipient.mutate({ id: recipient.id, active: !recipient.active })}>
                    {recipient.active ? "Pause" : "Activate"}
                  </button>
                  <button onClick={() => deleteRecipient.mutate(recipient.id)}><Trash2 size={16} /> Delete</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
      {selectedAlertId && (
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Incident detalj</h2>
              <p>Low-score razlozi, komentar i kontakt podaci uz PII scoping i audit log.</p>
            </div>
            <button type="button" onClick={() => setSelectedAlertId("")}>Zatvori</button>
          </div>
          {alertDetail.isLoading && <p>Ucitavanje detalja...</p>}
          {alertDetail.isError && <p className="form-error">Detalj nije dostupan za trenutni scope.</p>}
          {alertDetail.data && <AlertDetailPanel detail={alertDetail.data} />}
        </section>
      )}
    </div>
  );
}

function AlertDetailPanel({ detail }: { detail: AlertDetail }) {
  const followUp = detail.lowScoreFollowUp;
  return (
    <div className="grid-two narrow">
      <section className="incident-detail-block">
        <h3>Context</h3>
        <div className="rule-meta">
          <span>{detail.severity}</span>
          <span>{detail.status}</span>
          <span>{detail.submission.language}</span>
          <span>{detail.submission.deviceType ?? "device n/a"}</span>
        </div>
        <p><strong>{detail.hotel.name}</strong> - {detail.location.name}{detail.submission.subLocation ? ` - ${detail.submission.subLocation.name}` : ""}</p>
        <p>QR: <code>{detail.submission.requestPath ?? "-"}</code></p>
        <p>Unit: {detail.submission.unitCode ?? "-"}</p>
        <p>Meal: {detail.submission.mealPeriod ?? "-"}</p>
      </section>

      <section className="incident-detail-block">
        <h3>Low-score follow-up</h3>
        {followUp ? (
          <>
            <p>Kategorije: {followUp.triggeredCategories.join(", ") || "-"}</p>
            <p>Razlozi: {followUp.selectedReasons.join(", ") || "-"}</p>
            <p>Kontakt dozvola: {followUp.contactPermission ? "Da" : "Ne"}</p>
            {followUp.commentRedacted ? (
              <p className="status">Komentar je redacted za ovaj nalog.</p>
            ) : (
              <p>{followUp.comment || "Komentar nije unesen."}</p>
            )}
          </>
        ) : (
          <p>Nema low-score follow-up zapisa.</p>
        )}
      </section>

      <section className="incident-detail-block">
        <h3>Kontakt</h3>
        <p>Contact permission: {detail.submission.contactPermission ? "Da" : "Ne"}</p>
        <p>Soba: {piiValue(detail.submission.roomNumber, detail.submission.roomRedacted, "pii.view_room")}</p>
        <p>Ime i prezime: {piiValue(detail.submission.contactName, detail.submission.contactRedacted, "pii.view_contact")}</p>
        <p>Email: {piiValue(detail.submission.contactEmail, detail.submission.contactRedacted, "pii.view_contact")}</p>
        <p>Telefon: {piiValue(detail.submission.contactPhone, detail.submission.contactRedacted, "pii.view_contact")}</p>
      </section>

      <section className="incident-detail-block">
        <h3>Odgovori</h3>
        <div className="table-shell">
          <table>
            <thead>
              <tr>
                <th>Pitanje</th>
                <th>Tip</th>
                <th>Vrijednost</th>
              </tr>
            </thead>
            <tbody>
              {detail.answers.map((answer, index) => (
                <tr key={`${answer.questionKey}-${answer.answerType}-${index}`}>
                  <td>{answer.questionKey}</td>
                  <td>{answer.answerType}</td>
                  <td>{answerValue(answer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function answerValue(answer: AlertDetail["answers"][number]) {
  if (answer.numericValue !== null && answer.numericValue !== undefined) return answer.numericValue.toString();
  if (answer.booleanValue !== null && answer.booleanValue !== undefined) return answer.booleanValue ? "Da" : "Ne";
  if (answer.optionValue) return answer.optionValue;
  if (answer.textRedacted) return "[redacted]";
  if (answer.textValue) return answer.textValue;
  return "-";
}

function piiValue(value: string | null | undefined, redacted: boolean | undefined, permission: string) {
  if (value) return value;
  if (redacted) return `Sakriveno: nalog nema ${permission}.`;
  return "-";
}
